'use client';
/*
 * Documentation:
 * Tabs — https://app.subframe.com/6fc7f5fbb098/library?component=Tabs_e1ad5091-8ad8-4319-b1f7-3e47f0256c20
 */

import * as SubframeCore from '@subframe/core';
import React from 'react';
import * as SubframeUtils from '../utils';

const TAB_SELECTOR = '[role="tab"]:not([aria-disabled="true"])';

interface ItemProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  value?: string;
  className?: string;
}

function isActivationKey(key: string): boolean {
  return key === 'Enter' || key === ' ';
}

/**
 * Aba com semântica WAI-ARIA (`role="tab"`, `aria-selected`, tabindex roving). Enter/Espaço
 * disparam o mesmo `onClick` do mouse — por isso o clique nativo, e não uma chamada direta:
 * quem consome continua recebendo um `MouseEvent`. Atributos ARIA/teclado passados pelo
 * consumidor prevalecem sobre os padrões.
 */
const Item = React.forwardRef<HTMLDivElement, ItemProps>(function Item(
  {
    active = false,
    disabled = false,
    icon = null,
    children,
    value,
    className,
    onKeyDown,
    ...otherProps
  }: ItemProps,
  ref,
) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled || !isActivationKey(event.key)) return;
    event.preventDefault();
    event.currentTarget.click();
  };

  return (
    <div
      role="tab"
      aria-selected={active}
      aria-disabled={disabled || undefined}
      tabIndex={active ? 0 : -1}
      className={SubframeUtils.twClassNames(
        'group/d5612535 border-neutral-border flex h-10 cursor-pointer items-center justify-center gap-2 border-b border-solid px-2.5 py-0.5 transition-all',
        {
          'border-brand-600 hover:border-brand-600 border-b-2 border-solid px-2.5 pt-0.5 pb-px hover:border-b-2 hover:border-solid':
            active,
        },
        className,
      )}
      ref={ref}
      onKeyDown={handleKeyDown}
      {...otherProps}
    >
      {icon ? (
        <SubframeCore.IconWrapper
          className={SubframeUtils.twClassNames(
            'text-body font-body text-subtext-color group-hover/d5612535:text-default-font',
            {
              'text-neutral-400 group-hover/d5612535:text-neutral-400': disabled,
              'text-brand-700 group-hover/d5612535:text-brand-700': active,
            },
          )}
        >
          {icon}
        </SubframeCore.IconWrapper>
      ) : null}
      {children ? (
        <div
          className={SubframeUtils.twClassNames(
            'text-body font-body text-subtext-color group-hover/d5612535:text-default-font flex items-center gap-2',
            {
              'text-neutral-400 group-hover/d5612535:text-neutral-400': disabled,
              'text-brand-700 group-hover/d5612535:text-brand-700': active,
            },
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
});

interface TabsRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
  /** Classes da linha que agrupa os itens (ex.: `flex-wrap` para quebrar abas em tela estreita). */
  listClassName?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

function isItemElement(child: React.ReactNode): child is React.ReactElement<ItemProps> {
  return React.isValidElement(child);
}

function isItemActive(item: React.ReactElement<ItemProps>, value: string | undefined): boolean {
  if (item.props.active !== undefined) return item.props.active;
  return value !== undefined && item.props.value === value;
}

function nextFocusIndex(key: string, currentIndex: number, total: number): number | null {
  if (key === 'ArrowRight') return (currentIndex + 1) % total;
  if (key === 'ArrowLeft') return (currentIndex - 1 + total) % total;
  if (key === 'Home') return 0;
  if (key === 'End') return total - 1;
  return null;
}

/**
 * `aria-label`/`aria-labelledby` vão para o elemento `tablist` (a linha dos itens), e não para o
 * wrapper externo, para que o leitor de tela anuncie o nome do grupo de abas.
 */
const TabsRoot = React.forwardRef<HTMLDivElement, TabsRootProps>(function TabsRoot(
  {
    children,
    className,
    listClassName,
    value,
    onValueChange,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    ...otherProps
  }: TabsRootProps,
  ref,
) {
  const childArray = React.Children.toArray(children);
  const hasActiveItem = childArray.some(
    (child) => isItemElement(child) && isItemActive(child, value),
  );
  const fallbackFocusIndex = hasActiveItem ? -1 : childArray.findIndex(isItemElement);

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(TAB_SELECTOR));
    const currentIndex = tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex === -1) return;
    const targetIndex = nextFocusIndex(event.key, currentIndex, tabs.length);
    if (targetIndex === null) return;
    event.preventDefault();
    tabs[targetIndex].focus();
  };

  // Sem aba ativa, a primeira precisa ficar alcançável pelo Tab; senão o grupo some do teclado.
  const decorateItem = (child: React.ReactNode, index: number) => {
    if (!isItemElement(child)) return child;
    const focusProps = index === fallbackFocusIndex ? { tabIndex: child.props.tabIndex ?? 0 } : {};
    if (value === undefined) return React.cloneElement(child, focusProps);
    return React.cloneElement(child, {
      ...focusProps,
      active: isItemActive(child, value),
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        if (child.props.onClick) child.props.onClick(e);
        if (onValueChange && child.props.value) {
          onValueChange(child.props.value);
        }
      },
    });
  };

  return (
    <div
      className={SubframeUtils.twClassNames('flex w-full items-end', className)}
      ref={ref}
      {...otherProps}
    >
      {children ? (
        <div
          role="tablist"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={SubframeUtils.twClassNames(
            'flex w-full items-start self-stretch',
            listClassName,
          )}
          onKeyDown={handleListKeyDown}
        >
          {childArray.map(decorateItem)}
        </div>
      ) : null}
      <div className="border-neutral-border flex shrink-0 grow basis-0 flex-col items-start gap-2 self-stretch border-b border-solid" />
    </div>
  );
});

export const Tabs = Object.assign(TabsRoot, {
  Item,
});
